// https://github.com/biati-digital/glightbox
import 'glightbox/dist/css/glightbox.css';
import GLightbox from 'glightbox';

// Cache configuration
const CACHE_KEY_PREFIX = 'glightbox_video_ratio_';
const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

// Global state
let isInitialized = false;
let initializationPromise = null;
let glightboxInstances = [];

// Function to get cached video ratio
function getCachedVideoRatio(url) {
    try {
        const cacheKey = CACHE_KEY_PREFIX + btoa(url).replace(/[^a-zA-Z0-9]/g, '');
        const cached = localStorage.getItem(cacheKey);
        
        if (cached) {
            const { ratio, timestamp } = JSON.parse(cached);
            const now = Date.now();
            
            // Check if cache is still valid (within CACHE_DURATION)
            if (now - timestamp < CACHE_DURATION) {
                console.log(`Using cached ratio ${ratio} for: ${url}`);
                return ratio;
            } else {
                // Cache expired, remove it
                localStorage.removeItem(cacheKey);
                console.log(`Cache expired for: ${url}`);
            }
        }
    } catch (error) {
        console.log('Error reading from cache:', error);
    }
    
    return null;
}

// Function to cache video ratio
function cacheVideoRatio(url, ratio) {
    try {
        const cacheKey = CACHE_KEY_PREFIX + btoa(url).replace(/[^a-zA-Z0-9]/g, '');
        const cacheData = {
            ratio: ratio,
            timestamp: Date.now()
        };
        
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
        console.log(`Cached ratio ${ratio} for: ${url}`);
    } catch (error) {
        console.log('Error writing to cache:', error);
    }
}

// Function to get Vimeo video info
async function getVimeoVideoInfo(videoId) {
    try {
        const response = await fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${videoId}`);
        const data = await response.json();
        return {
            width: data.width,
            height: data.height,
            ratio: simplifyRatio(data.width, data.height)
        };
    } catch (error) {
        console.log('Could not fetch Vimeo video info, using default ratio');
        return { ratio: '16:9' };
    }
}

// Function to get YouTube video info
async function getYouTubeVideoInfo(videoId) {
    try {
        // Most YouTube videos are 16:9
        // You can enhance this with YouTube Data API if needed
        return { ratio: '16:9' };
    } catch (error) {
        console.log('Could not fetch YouTube video info, using default ratio');
        return { ratio: '16:9' };
    }
}

// Function to get video ratio from a video element
function getVideoElementRatio(videoUrl) {
    return new Promise((resolve) => {
        const tempVideo = document.createElement('video');
        tempVideo.style.display = 'none';
        tempVideo.preload = 'metadata';
        tempVideo.crossOrigin = 'anonymous';
        
        tempVideo.addEventListener('loadedmetadata', () => {
            if (tempVideo.videoWidth && tempVideo.videoHeight) {
                const ratio = simplifyRatio(tempVideo.videoWidth, tempVideo.videoHeight);
                document.body.removeChild(tempVideo);
                resolve(ratio);
            } else {
                document.body.removeChild(tempVideo);
                resolve('16:9');
            }
        });
        
        tempVideo.addEventListener('error', () => {
            if (tempVideo.parentNode) {
                document.body.removeChild(tempVideo);
            }
            resolve('16:9');
        });
        
        // Fallback timeout
        setTimeout(() => {
            if (tempVideo.parentNode) {
                document.body.removeChild(tempVideo);
            }
            resolve('16:9');
        }, 5000);
        
        document.body.appendChild(tempVideo);
        tempVideo.src = videoUrl;
    });
}

// Function to extract video ID and type from URL
function extractVideoInfo(url) {
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vimeoMatch) {
        return { type: 'vimeo', id: vimeoMatch[1] };
    }
    
    // YouTube
    const youtubeMatch = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    if (youtubeMatch) {
        return { type: 'youtube', id: youtubeMatch[1] };
    }
    
    // Self-hosted video
    if (url.match(/\.(mp4|webm|ogg|mov|avi)(\?.*)?$/i)) {
        return { type: 'video', url: url };
    }
    
    return null;
}

// Function to calculate simplified ratio
function simplifyRatio(width, height) {
    const gcd = (a, b) => b === 0 ? a : gcd(b, a % b);
    const divisor = gcd(width, height);
    return `${width / divisor}:${height / divisor}`;
}

// Function to detect video ratio with caching
async function detectVideoRatio(url) {
    // Check cache first
    const cachedRatio = getCachedVideoRatio(url);
    if (cachedRatio) {
        return cachedRatio;
    }
    
    const videoInfo = extractVideoInfo(url);
    
    if (!videoInfo) {
        return '16:9'; // Default for non-video content
    }
    
    try {
        let ratio = '16:9';
        
        switch (videoInfo.type) {
            case 'vimeo':
                const vimeoInfo = await getVimeoVideoInfo(videoInfo.id);
                ratio = vimeoInfo.ratio;
                console.log(`Detected Vimeo video ratio: ${ratio} for video ${videoInfo.id}`);
                break;
                
            case 'youtube':
                const youtubeInfo = await getYouTubeVideoInfo(videoInfo.id);
                ratio = youtubeInfo.ratio;
                console.log(`Detected YouTube video ratio: ${ratio} for video ${videoInfo.id}`);
                break;
                
            case 'video':
                ratio = await getVideoElementRatio(videoInfo.url);
                console.log(`Detected self-hosted video ratio: ${ratio} for ${videoInfo.url}`);
                break;
        }
        
        // Cache the result
        cacheVideoRatio(url, ratio);
        return ratio;
        
    } catch (error) {
        console.log('Error detecting video ratio:', error);
        return '16:9';
    }
}

// Function to show loading indicator
function showLoadingIndicator() {
    if (document.getElementById('glightbox-loading')) return;
    
    const loading = document.createElement('div');
    loading.id = 'glightbox-loading';
    loading.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: rgba(0, 0, 0, 0.8);
        color: white;
        padding: 10px 15px;
        border-radius: 5px;
        font-size: 14px;
        z-index: 9999;
        display: flex;
        align-items: center;
        gap: 10px;
    `;
    loading.innerHTML = `
        <div style="width: 16px; height: 16px; border: 2px solid #ffffff; border-top: 2px solid transparent; border-radius: 50%; animation: spin 1s linear infinite;"></div>
        Analyzing videos...
    `;
    
    // Add CSS for spinner animation
    if (!document.getElementById('glightbox-spinner-css')) {
        const style = document.createElement('style');
        style.id = 'glightbox-spinner-css';
        style.textContent = `
            @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
        `;
        document.head.appendChild(style);
    }
    
    document.body.appendChild(loading);
}

// Function to hide loading indicator
function hideLoadingIndicator() {
    const loading = document.getElementById('glightbox-loading');
    if (loading) {
        loading.remove();
    }
}

// Function to scan and group elements by ratio
async function scanAndGroupVideoElements() {
    const videoElements = document.querySelectorAll('.glightbox');
    const ratioGroups = {};
    const nonVideoElements = [];
    
    console.log(`Found ${videoElements.length} lightbox elements to scan`);
    
    // Show loading indicator if there are videos to scan
    const videoUrls = Array.from(videoElements)
        .map(el => el.getAttribute('href') || el.getAttribute('data-src'))
        .filter(href => href && extractVideoInfo(href));
    
    if (videoUrls.length > 0) {
        showLoadingIndicator();
    }
    
    for (const element of videoElements) {
        const href = element.getAttribute('href') || element.getAttribute('data-src');
        
        if (href) {
            const videoInfo = extractVideoInfo(href);
            
            if (videoInfo) {
                // This is a video element
                const ratio = await detectVideoRatio(href);
                
                if (!ratioGroups[ratio]) {
                    ratioGroups[ratio] = [];
                }
                
                // Add unique class for this ratio
                const ratioClass = `glightbox-ratio-${ratio.replace(':', '-')}`;
                element.classList.add(ratioClass);
                ratioGroups[ratio].push(element);
                
                console.log(`Added video to ${ratio} group:`, href);
            } else {
                // This is not a video (image, iframe, etc.)
                nonVideoElements.push(element);
            }
        }
    }
    
    // Hide loading indicator
    hideLoadingIndicator();
    
    return { ratioGroups, nonVideoElements };
}

// Function to create GLightbox instances
function createGLightboxInstances(ratioGroups, nonVideoElements) {
    const instances = [];
    
    // Create instances for each video ratio group
    Object.keys(ratioGroups).forEach(ratio => {
        const elements = ratioGroups[ratio];
        
        if (elements.length > 0) {
            const ratioClass = `glightbox-ratio-${ratio.replace(':', '-')}`;
            
            console.log(`Creating GLightbox instance for ratio ${ratio} with ${elements.length} elements`);
            
            const instance = GLightbox({
                selector: `.${ratioClass}`,
                plyr: {
                    config: {
                        ratio: ratio
                    }
                }
            });
            
            instances.push(instance);
        }
    });
    
    // Create instance for non-video elements (images, etc.)
    if (nonVideoElements.length > 0) {
        nonVideoElements.forEach(el => el.classList.add('glightbox-non-video'));
        
        console.log(`Creating GLightbox instance for non-video content with ${nonVideoElements.length} elements`);
        
        const nonVideoInstance = GLightbox({
            selector: '.glightbox-non-video'
        });
        
        instances.push(nonVideoInstance);
    }
    
    return instances;
}

// Main initialization function
async function initializeGLightbox() {
    if (isInitialized) {
        return glightboxInstances;
    }
    
    if (initializationPromise) {
        return initializationPromise;
    }
    
    initializationPromise = (async () => {
        console.log('Starting GLightbox initialization with automatic video ratio detection...');
        
        try {
            const { ratioGroups, nonVideoElements } = await scanAndGroupVideoElements();
            const instances = createGLightboxInstances(ratioGroups, nonVideoElements);
            
            console.log(`GLightbox initialization complete. Created ${instances.length} instances.`);
            
            isInitialized = true;
            glightboxInstances = instances;
            return instances;
            
        } catch (error) {
            console.error('Error during GLightbox initialization:', error);
            
            // Fallback: create a single instance with default settings
            console.log('Creating fallback GLightbox instance...');
            const fallbackInstance = GLightbox({
                selector: '.glightbox',
                plyr: {
                    config: {
                        ratio: '16:9'
                    }
                }
            });
            
            isInitialized = true;
            glightboxInstances = [fallbackInstance];
            return [fallbackInstance];
        }
    })();
    
    return initializationPromise;
}

// Function to ensure initialization before opening lightbox
async function ensureInitialized() {
    if (!isInitialized) {
        console.log('GLightbox not yet initialized, waiting...');
        await initializeGLightbox();
    }
}

// Override click events to ensure initialization
function interceptLightboxClicks() {
    document.addEventListener('click', async (e) => {
        const target = e.target.closest('.glightbox');
        if (target && !isInitialized) {
            e.preventDefault();
            e.stopPropagation();
            
            console.log('Intercepted lightbox click, ensuring initialization...');
            await ensureInitialized();
            
            // Trigger the click again after initialization
            setTimeout(() => {
                target.click();
            }, 100);
        }
    }, true); // Use capture phase to intercept early
}

// Initialize when DOM is ready
$(document).ready(async function() {
    // Set up click interception immediately
    interceptLightboxClicks();
    
    // Start initialization in background
    console.log('Starting background GLightbox initialization...');
    initializeGLightbox().then(() => {
        console.log('Background initialization completed!');
    });
    
    // Search box header (separate instance)
    const searchbox = GLightbox({
        touchNavigation: false,
        touchFollowAxis: false,
        keyboardNavigation: false,
        selector: ".searchboxlink",
        zoomable: false,
        draggable: false,
        height: 'auto',
        width: '860px',
        skin: 'header-search',
    });
    
    searchbox.on('open', () => {
        setTimeout(() => {
            const $input = $('.glightbox-container .header-search-modal input[type="search"]').first();
            if ($input.length) {
                $input.trigger('focus');
            }
        }, 150);
        $('.glightbox-container .button--close').off('click.lmSearchClose').on('click.lmSearchClose', function() {
            searchbox.close();
        });
    });
});
